import React from 'react';
import ProductCard from './ProductCard';
import { ProductCardSkeletonGrid } from './ProductCardSkeleton';
import { EmptyState, ErrorState } from './States';
import useGridColumns, { wholeRows } from '../../hooks/useGridColumns';

/**
 * Renders a list of products with its loading / error / empty states, so
 * every listing page behaves the same way instead of each inventing its own.
 *
 * `layout`:
 *   grid     — fluid auto-fill columns (listing pages, search, wishlist)
 *   featured — fixed 5/4/3/2 columns, centred, with the trailing partial
 *              row trimmed so the block is always a clean rectangle
 *   rail     — horizontally scrolling strip
 */
const LAYOUT_CLASS = {
  grid: 'ij-product-grid',
  featured: 'ij-product-grid ij-product-grid--featured',
  rail: 'ij-rail',
};

const ProductGrid = ({
  products = [],
  loading = false,
  error = null,
  onRetry,
  skeletonCount = 8,
  emptyTitle = 'No products found',
  emptyDescription = 'Try adjusting your filters or browse another category.',
  emptyAction,
  linkFor,
  isWishlisted,
  onToggleWishlist,
  wishlistPending,
  onAddToCart,
  cartPending,
  layout = 'grid',
  className,
}) => {
  const columns = useGridColumns();
  const balanced = layout === 'featured';
  const gridClass = className || LAYOUT_CLASS[layout] || LAYOUT_CLASS.grid;

  // Tells the featured grid how many cards it is actually laying out, so a
  // section with fewer products than one row narrows rather than stretches.
  const gridStyle = (count) =>
    balanced ? { '--ij-grid-columns': Math.min(count, columns) } : undefined;

  if (loading) {
    const skeletons = balanced ? wholeRows(skeletonCount, columns) : skeletonCount;
    return (
      <div className={gridClass} style={gridStyle(skeletons)} aria-busy="true">
        <ProductCardSkeletonGrid count={skeletons} />
      </div>
    );
  }

  if (error) return <ErrorState description={error} onRetry={onRetry} />;

  if (products.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  // A row of 5 followed by a lone 4th card looks broken, so drop the
  // leftovers rather than leave the last row ragged.
  const visible = balanced
    ? products.slice(0, wholeRows(products.length, columns))
    : products;

  return (
    <div className={gridClass} style={gridStyle(visible.length)}>
      {visible.map((product) => (
        <ProductCard
          key={product.id ?? product.product_code}
          product={product}
          to={linkFor?.(product)}
          isWishlisted={isWishlisted?.(product.product_code)}
          onToggleWishlist={onToggleWishlist}
          wishlistPending={wishlistPending === product.product_code}
          onAddToCart={onAddToCart}
          cartPending={cartPending === product?.variant?.id}
        />
      ))}
    </div>
  );
};

export default ProductGrid;
